/**
 * OrgWinsStrip — emerald-tinted "Wins this week" strip rendered
 * alongside the `<OrgHealthTriageWidget>`. Same audience (org admin),
 * opposite emotion: celebrates positive 7-day deltas (teammates
 * onboarded, invitations completed, failed-login-free streak)
 * so the home page stays balanced rather than feeling purely
 * remediation-focused.
 *
 * Self-hides when every win is zero/null so empty tenants don't
 * see "0 wins" — that's not a celebration, that's noise.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Sparkles, UserPlus, MailCheck, ShieldCheck, RefreshCw } from 'lucide-react';
import api from '../../lib/api';

const POLL_INTERVAL_MS = 90_000;

const OrgWinsStrip = () => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const fetchWins = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/admin/platform-health/org-wins');
      setData(res?.data || null);
    } catch {
      // Silent on transient blips
    } finally {
      setLoaded(true);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchWins();
    const onVisibility = () => { if (!document.hidden) fetchWins(); };
    document.addEventListener('visibilitychange', onVisibility);
    intervalRef.current = setInterval(() => {
      if (!document.hidden) fetchWins();
    }, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchWins]);

  if (!loaded || !data) return null;
  const w = data.wins || {};
  const teammates = w.teammates_added || 0;
  const invitations = w.invitations_completed || 0;
  const cleanHours = w.failed_login_free_hours;

  // Self-hide when there's nothing to celebrate. Hours-clean alone
  // counts as a win when ≥24h — a fresh streak deserves the nod.
  const hasWin = teammates > 0
    || invitations > 0
    || (typeof cleanHours === 'number' && cleanHours >= 24);
  if (!hasWin) return null;

  const cleanCopy = cleanHours == null
    ? null
    : (cleanHours >= 168
      ? '7+ days failed-login-free'
      : `${cleanHours}h failed-login-free`);

  return (
    <Card
      className="mb-6 border-emerald-200 bg-emerald-50/60 ring-1 ring-emerald-200"
      data-testid="org-wins-strip"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            <span className="text-slate-900">Wins this week</span>
            {refreshing && <RefreshCw className="h-3 w-3 text-slate-400 animate-spin ml-1" />}
          </CardTitle>
          <span className="text-[11px] text-slate-500">last 7 days</span>
        </div>
        <CardDescription className="text-slate-600">
          What your team achieved this week — celebrate forward motion alongside the triage panel.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {teammates > 0 && (
            <div
              className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-white"
              data-testid="org-win-teammates"
            >
              <div className="p-1.5 rounded shrink-0 bg-emerald-50">
                <UserPlus className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-900 leading-none">+{teammates}</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  teammate{teammates === 1 ? '' : 's'} onboarded
                </p>
              </div>
            </div>
          )}
          {invitations > 0 && (
            <div
              className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-white"
              data-testid="org-win-invitations"
            >
              <div className="p-1.5 rounded shrink-0 bg-emerald-50">
                <MailCheck className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-900 leading-none">{invitations}</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  invitation{invitations === 1 ? '' : 's'} completed
                </p>
              </div>
            </div>
          )}
          {cleanCopy && (
            <div
              className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-white"
              data-testid="org-win-clean-streak"
            >
              <div className="p-1.5 rounded shrink-0 bg-emerald-50">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-900 leading-tight">
                  {cleanCopy.split(' ')[0]}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {cleanCopy.split(' ').slice(1).join(' ')}
                </p>
              </div>
              {cleanHours >= 168 && (
                <Badge className="ml-auto bg-emerald-100 text-emerald-800 border border-emerald-300">
                  CLEAN
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OrgWinsStrip;
